import { useMutation } from '@tanstack/react-query'
import { Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import type { MockImportOutcome, MocksApi } from '../api/mocks'

/**
 * One ZIP of preassembled collections, previewed in full before anything is written (app:R29).
 *
 * THE FOLDERS SAY EVERYTHING, so there is nothing to choose here. Each file's path names its Kind,
 * its Paper and, for a split Paper, its Session -- Mocks/P1/001/, MiniMocks/P2/003/,
 * Official/P1/2019/ -- and the API reads them from there and from nowhere else. A choice on this
 * screen could only disagree with the folders, so the screen offers none, and the preview table is
 * where what the folders said is checked.
 */
export function MockUpload({ exam, mocks, onImported }: { exam: string; mocks: MocksApi; onImported: () => void }) {
  const picker = useRef<HTMLInputElement>(null)
  const [over, setOver] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [checked, setChecked] = useState<MockImportOutcome | null>(null)
  const [imported, setImported] = useState(false)
  const [selectionError, setSelectionError] = useState<string | null>(null)
  const upload = useMutation({
    mutationFn: ({ file, preview }: { file: File; preview: boolean }) => mocks.upload(exam, file, preview),
    onSuccess: (outcome) => {
      setChecked(outcome)
      if (outcome.accepted && !outcome.preview) {
        setImported(true)
        onImported()
      }
    },
  })

  function check(chosen: File | null) {
    setFile(chosen)
    setChecked(null)
    setImported(false)
    setSelectionError(null)
    upload.reset()
    if (chosen) upload.mutate({ file: chosen, preview: true })
  }

  function choose(files: FileList | null) {
    if (upload.isPending) return
    if (files && files.length > 1) {
      setSelectionError('Put the folders together in one ZIP, then upload that ZIP.')
      setChecked(null)
      return
    }
    check(files?.[0] ?? null)
  }

  return (
    <section aria-label="Upload Mocks"
      onDragOver={(event) => { event.preventDefault(); setOver(true) }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => { event.preventDefault(); setOver(false); choose(event.dataTransfer.files) }}
      className={`rounded-lg border-2 border-dashed p-4 ${over ? 'border-green-800 bg-green-050' : 'border-ink-100'}`}>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className={quiet} disabled={upload.isPending} onClick={() => picker.current?.click()}>
          <Upload aria-hidden size={16} /> Upload files
        </button>
        <input ref={picker} type="file" accept=".zip" disabled={upload.isPending} className="sr-only" aria-label="Mock archive"
          onChange={(event) => { choose(event.target.files); event.target.value = '' }} />
        <span className="text-muted">or drop a ZIP here</span>
      </div>
      <p className="mt-2 text-muted">
        Keep the folders: <code>Mocks/P1/001/</code>, <code>MiniMocks/P1/001/</code> or <code>Official/P1/2019/</code>.
        One ZIP can hold all three.
      </p>
      {file && <p className="mt-3 text-muted">{file.name}</p>}
      {upload.isPending && <p role="status" className="mt-3 text-muted">{upload.variables?.preview ? 'Checking the ZIP...' : 'Importing the Mocks...'}</p>}
      {(upload.error || selectionError) && <p role="alert" className="mt-3 text-red-700">{selectionError ?? upload.error?.message}</p>}
      {checked && <>
        {checked.collections.length > 0 && <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left">
            <caption className="mb-2 text-left font-bold">{imported ? 'Imported collections' : 'Archive contents'}</caption>
            <thead><tr>{['Name', 'Paper', 'Kind', 'Questions'].map((heading) => <th key={heading} scope="col" className="border-b border-ink-100 p-2">{heading}</th>)}</tr></thead>
            <tbody>{checked.collections.map((collection) => <tr key={`${collection.kind}/${collection.componentCode}/${collection.session}/${collection.title}`}>
              <td className="p-2">{collection.title}</td>
              <td className="p-2">{collection.componentCode}{collection.session === 'Default' ? '' : ` / ${collection.session}`}</td>
              <td className="p-2">{kindName(collection.kind)}</td>
              <td className="p-2">{collection.questionCount}</td>
            </tr>)}</tbody>
          </table>
        </div>}
        {checked.errors.length > 0 && <div role="alert" className="mt-3 text-red-700"><p>Nothing was imported.</p><ul className="list-inside list-disc">{checked.errors.map((error, index) => <li key={index}>{error}</li>)}</ul></div>}
        {(checked.excludedFiles?.length ?? 0) > 0 && <details className="mt-3 text-muted">
          <summary>{checked.excludedFiles?.length} authoring notes left out</summary>
          <ul className="mt-1 list-inside list-disc">{checked.excludedFiles?.map((name) => <li key={name}>{name}</li>)}</ul>
        </details>}
      </>}
      {imported && <p role="status" className="mt-3 text-green-800">All Mocks in the ZIP have been imported.</p>}
      {checked?.accepted && !imported && <button type="button" disabled={upload.isPending} onClick={() => upload.mutate({ file: file!, preview: false })}
        className="mt-3 rounded-md bg-green-800 px-4 py-2 font-bold text-white disabled:opacity-55">Import Drafts</button>}
    </section>
  )
}

function kindName(kind: string): string {
  return kind === 'Mini' ? 'Mini-Mock' : kind === 'Official' ? 'Official' : 'Mock'
}

const quiet = 'inline-flex items-center gap-2 rounded-md border border-green-800 px-3 py-1.5 font-bold text-green-800 hover:bg-green-050 disabled:opacity-55'
